import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { User, LaserMachine, Job, HMIMessage, UserPermissions, AppNotification } from '../types';
import { authService, machineService, messageService } from '../services/api';
import { getUserPermissions, filterMachinesByRole } from '../utils/permissions';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

interface AppState {
  auth: AuthState;
  machines: LaserMachine[];
  machinesLoading: boolean;
  machinesError: string | null;
  machinesUpdatedAt: string | null;
  jobs: Job[];
  messages: HMIMessage[];
  notifications: AppNotification[];
  selectedMachine: LaserMachine | null;
  permissions: UserPermissions;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MACHINES'; payload: LaserMachine[] }
  | { type: 'MACHINES_LOADING' }
  | { type: 'MACHINES_ERROR'; payload: string }
  | { type: 'SET_JOBS'; payload: Job[] }
  | { type: 'SET_MESSAGES'; payload: HMIMessage[] }
  | { type: 'SELECT_MACHINE'; payload: LaserMachine | null }
  | { type: 'UPDATE_MACHINE'; payload: LaserMachine }
  | { type: 'ADD_MESSAGE'; payload: HMIMessage }
  | { type: 'UPDATE_JOB'; payload: Job }
  | { type: 'ADD_NOTIFICATION'; payload: AppNotification }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'MARK_ALL_NOTIFICATIONS_READ' }
  | { type: 'LOGOUT' };

const initialState: AppState = {
  auth: {
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
  },
  machines: [],
  machinesLoading: false,
  machinesError: null,
  machinesUpdatedAt: null,
  jobs: [],
  messages: [],
  notifications: [],
  selectedMachine: null,
  permissions: getUserPermissions('client'), // Default permissions
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        auth: { ...state.auth, loading: action.payload },
      };

    case 'SET_USER':
      return {
        ...state,
        auth: {
          user: action.payload,
          isAuthenticated: !!action.payload,
          loading: false,
          error: null,
        },
        permissions: action.payload ? getUserPermissions(action.payload.role) : getUserPermissions('client'),
      };

    case 'SET_ERROR':
      return {
        ...state,
        auth: { ...state.auth, error: action.payload, loading: false },
      };

    case 'SET_MACHINES':
      return { ...state, machines: action.payload, machinesLoading: false, machinesError: null, machinesUpdatedAt: new Date().toISOString() };
    case 'MACHINES_LOADING':
      return { ...state, machinesLoading: true };
    case 'MACHINES_ERROR':
      return { ...state, machinesLoading: false, machinesError: action.payload };

    case 'SET_JOBS':
      return { ...state, jobs: action.payload };

    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };

    case 'SELECT_MACHINE':
      return { ...state, selectedMachine: action.payload };

    case 'UPDATE_MACHINE':
      return {
        ...state,
        machines: state.machines.map(machine =>
          machine.id === action.payload.id ? action.payload : machine
        ),
        selectedMachine: state.selectedMachine?.id === action.payload.id
          ? action.payload
          : state.selectedMachine,
      };

    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [action.payload, ...state.messages],
      };

    case 'UPDATE_JOB':
      return {
        ...state,
        jobs: state.jobs.map(job =>
          job.id === action.payload.id ? action.payload : job
        ),
      };

    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 50),
      };

    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: true } : n
        ),
      };

    case 'MARK_ALL_NOTIFICATIONS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => ({ ...n, read: true })),
      };

    case 'LOGOUT':
      return {
        ...initialState,
        auth: { ...initialState.auth, loading: false },
      };

    default:
      return state;
  }
};

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  login: (email: string, password: string) => Promise<void>;
  loginWithSession: (token: string, backendUser: any) => void;
  logout: () => Promise<void>;
  refreshMachines: () => Promise<void>;
  refreshMessages: (machineId: string) => Promise<void>;
  selectMachine: (machine: LaserMachine | null) => void;
  addNotification: (notification: AppNotification) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  unreadNotificationsCount: number;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const normalizeRole = (role?: string): User['role'] => {
  const normalized = (role || '').toLowerCase();
  if (normalized === 'customer') return 'client';
  if (normalized === 'administrator' || normalized === 'reseller' || normalized === 'client' || normalized === 'operator' || normalized === 'viewer') {
    return normalized;
  }
  return 'viewer';
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Establish a client-side session from a backend token + user payload.
  // Shared by password login, passkey login and QR cross-device login.
  const loginWithSession = (token: string, backendUser: any) => {
    if (!token || !backendUser) {
      throw new Error('Invalid login response');
    }
    localStorage.setItem('authToken', token);

    const user: User = {
      id: backendUser.id,
      name: backendUser.displayName || backendUser.name || 'Utente',
      email: backendUser.email,
      companyId: backendUser.companyId || '',
      role: normalizeRole(backendUser.roles?.[0] || backendUser.role),
      clientIds: backendUser.clientIds,
      resellerId: backendUser.resellerId,
    };

    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'SET_USER', payload: user });
  };

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authService.login(email, password);
      const payload = (response as any)?.data ?? response;
      const data = payload?.data ?? payload;
      const token = data?.token;
      const backendUser = data?.user ?? data;
      loginWithSession(token, backendUser);
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.error?.message || 'Accesso non riuscito. Controlla email e password.' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshMachines = useCallback(async () => {
    if (!state.auth.user) return;
    dispatch({ type: 'MACHINES_LOADING' });
    try {
      const isAdmin = state.auth.user.role === 'administrator';
      const isReseller = state.auth.user.role === 'reseller';
      const targetCompanyId = (isAdmin || isReseller) ? 'all' : state.auth.user.companyId;
      const machines = await machineService.getMachines(targetCompanyId);

      // Backend already enforces tenant filtering for admin/reseller.
      // Keep client-side role filter only for non-admin/non-reseller users.
      const filteredMachines = (isAdmin || isReseller)
        ? machines
        : filterMachinesByRole(machines, state.auth.user);

      dispatch({ type: 'SET_MACHINES', payload: filteredMachines });
    } catch (error) {
      dispatch({ type: 'MACHINES_ERROR', payload: 'Impossibile aggiornare le macchine. I dati visualizzati potrebbero non essere recenti.' });
      console.error('Error refreshing machines:', error);
    }
  }, [state.auth.user, dispatch]);

  const refreshMessages = async (machineId: string) => {
    try {
      const messages = await messageService.getMessages(machineId);
      dispatch({ type: 'SET_MESSAGES', payload: messages });
    } catch (error) {
      console.error('Error refreshing messages:', error);
    }
  };

  const selectMachine = (machine: LaserMachine | null) => {
    dispatch({ type: 'SELECT_MACHINE', payload: machine });
    if (machine) {
      refreshMessages(machine.id);
    }
  };

  const addNotification = useCallback((notification: AppNotification) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
  }, []);

  const markNotificationRead = useCallback((id: string) => {
    dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id });
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    dispatch({ type: 'MARK_ALL_NOTIFICATIONS_READ' });
  }, []);

  const unreadNotificationsCount = state.notifications.filter(n => !n.read).length;

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');
      if (token && savedUser) {
        try {
          const user = JSON.parse(savedUser) as User;
          dispatch({ type: 'SET_USER', payload: user });
        } catch (error) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          dispatch({ type: 'SET_USER', payload: null });
        }
      } else {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (state.auth.isAuthenticated) {
      refreshMachines();
    }
  }, [state.auth.isAuthenticated, refreshMachines]);

  const value: AppContextType = {
    state,
    dispatch,
    login,
    loginWithSession,
    logout,
    refreshMachines,
    refreshMessages,
    selectMachine,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    unreadNotificationsCount,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
